import { Box, BoxProps, SystemStyleObject } from "@biblioteksentralen/react";
import { CombinedRichTextType } from "@libry-content/types";
import { PortableText, PortableTextProps, PortableTextReactComponents } from "@portabletext/react";
import { BlockContentContextProvider } from "./BlockContentContext";
import { LinkSerializer } from "./LinkSerializer";
import { TextGallerySerializer } from "./TextGallerySerializer";
import { TextLinkButtonSerializer } from "./TextLinkButtonSerializer";
import { TextTableSerializer } from "./TextTableSerializer";
import VideoEmbedSerializer from "./VideoEmbedSerializer";
import { StyleMap, mapStyles } from "./styleMap";

export const richTextStyle: SystemStyleObject = {
  maxWidth: "100%",
  "& > *": {
    maxWidth: "30rem",
    marginLeft: "auto",
    marginRight: "auto",
    lineHeight: "1.5",
  },
  "& > p": {
    marginBottom: "1em",
  },
  "& > h2": {
    "&:not(:first-child)": {
      // We don't want to add marginTop on the first element in a rich text. The first element might be a text-block, so we can't use :first-of-type-selector as this only works if the first element is a h2.
      marginTop: "calc(2vmin + 2rem)",
    },
    marginBottom: "0.2em",
    fontSize: "xl",
    fontWeight: 600,
  },
  "& > h3": {
    marginTop: "1em",
    fontSize: "md",
    fontWeight: 600,
  },
  "& > ul,& > ol": {
    paddingLeft: "1.5em",
    marginBottom: "2.5em",
    "& ul, & ol": {
      marginBottom: 0,
    },
    "& li": {
      paddingLeft: "0.3em",
      marginBottom: "0.3em",
    },
  },
  "& > ul": {
    listStyle: "disc",
    "& ul": {
      listStyle: "circle",
    },
  },
  "& > ol": {
    listStyle: "decimal",
  },
  "& > *:last-child": {
    marginBottom: "0",
  },
};

const components: Partial<PortableTextReactComponents> = {
  types: {
    table: TextTableSerializer,
    gallery: TextGallerySerializer,
    linkButton: TextLinkButtonSerializer,
    videoEmbed: VideoEmbedSerializer,
  },
  marks: {
    link: LinkSerializer,
  },
};

type Props = {
  blocks?: CombinedRichTextType;
  styleMap?: StyleMap;
  logInternalLinks?: boolean;
  galleryBackgroundColors?: Record<string, string>;
} & BoxProps;

function BlockContent({ blocks, styleMap, logInternalLinks, galleryBackgroundColors, ...BoxProps }: Props) {
  return (
    <BlockContentContextProvider logInternalLinks={logInternalLinks} galleryBackgroundColors={galleryBackgroundColors}>
      <Box css={richTextStyle} fontSize="lg" {...BoxProps}>
        <PortableText value={mapStyles(styleMap, blocks) as PortableTextProps["value"]} components={components} />
      </Box>
    </BlockContentContextProvider>
  );
}

export default BlockContent;
